import { FC, HTMLAttributes, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Flex, theme, Typography } from 'antd';
import cn from 'classnames';

import NoImageIcon from 'assets/images/no-image.svg?react';
import { useBrandsSlice } from 'entities/Brand';
import {
	ENotificationTypes,
	IExtra,
	IMPORTANT_TYPES,
	INotification,
	useNotificationsSlice,
	useSetRejectedMutation,
} from 'entities/Notification';
import { useCommonSlice, useInView, useMedia, useMeta, useTrans } from 'shared/hooks';
import { ELanguages } from 'shared/typings';
import { formatDate } from 'shared/utils';

import styles from './NotificationCard.module.scss';

interface INotificationCardProps extends HTMLAttributes<HTMLDivElement> {
	notification: INotification;
	size?: 'sm' | 'md';
	isDashboard?: boolean;
	appendReadNotification?: (id: number) => void;
}

const intersectionOptions = {
	threshold: 1.0,
	triggerOnce: true,
};

export const NotificationCard: FC<INotificationCardProps> = ({
	className,
	notification,
	size = 'md',
	isDashboard,
	appendReadNotification,
	...props
}) => {
	const isMd = useMedia();
	const isLg = useMedia('lg');
	const {
		token: { colorTextTertiary, colorTextQuaternary, colorBorderSecondary },
	} = theme.useToken();
	const { setNotificationsModalIsOpened } = useCommonSlice();
	const { notificationTypes, notificationCategories } = useTrans();
	const { i18n } = useTranslation();
	const { readNotifications } = useNotificationsSlice();
	const { id, read, type, brand, offer, source, category, created_at } = notification;
	const isRead = read || readNotifications[id];

	const cardRef = useRef(null);
	const inView = useInView(cardRef, intersectionOptions);

	const [setRejected, { isLoading: rejectedIsloading }] = useSetRejectedMutation();

	useEffect(() => {
		if (inView && !isRead && !isDashboard) {
			appendReadNotification?.(id);
		}
	}, [inView]); //eslint-disable-line

	const { setJoinedInviteOffer, setJoinedInviteBrand, setJoinedOffer, setOpenedOffer } = useBrandsSlice();
	const { brandsMap } = useMeta();
	const fullBrand = brandsMap?.[brand?.uuid as keyof typeof brandsMap];
	const fullOffer = fullBrand?.offers.find((o) => o.uuid === offer?.uuid);

	const isSmall = size === 'sm' || isMd || isDashboard;

	const extraFields = notificationTypes?.[type]?.extra;
	const descriptionExtra =
		extraFields?.[0] === 'all'
			? [notification?.extra || '']
			: extraFields?.map((field) => notification?.extra?.[field as keyof IExtra]);

	const isCustom = type === ENotificationTypes.CUSTOM;
	const customNotification = notification?.extra?.[i18n.language as ELanguages] || notification?.extra?.[ELanguages.RU];

	const NotificationDate = () => (
		<Typography.Text style={{ fontSize: isSmall ? 12 : 14, color: isRead ? colorTextQuaternary : colorTextTertiary }}>
			<Flex gap={isDashboard && isLg && !isMd ? 0 : 12} vertical={isDashboard && isLg && !isMd}>
				<span>{formatDate(new Date(created_at), 'long')}</span>
				{!(isDashboard && isLg && !isMd) && <span>•</span>}
				<span>{notificationCategories?.[category] || category}</span>
			</Flex>
		</Typography.Text>
	);

	return (
		<Flex
			{...props}
			ref={cardRef}
			gap={16}
			vertical={isDashboard}
			className={cn(className, styles.notificationCard, styles[size], {
				[styles.important]: IMPORTANT_TYPES[type],
				[styles.read]: isRead,
				[styles.dashboard]: isDashboard,
			})}
			style={{ borderColor: colorBorderSecondary }}>
			<Flex vertical={!isDashboard} gap={isDashboard ? 12 : 8} align="center">
				<div className={styles.icon}>
					{notificationTypes?.[type]?.icon || <NoImageIcon />}
					{isDashboard && !isRead && <Badge className={styles.badge} status={isRead ? 'default' : 'error'} />}
				</div>
				{!isDashboard && <Badge status={isRead ? 'default' : 'error'} />}
				{isDashboard && <NotificationDate />}
			</Flex>
			<Flex vertical gap={isSmall ? 8 : 12}>
				<Flex vertical gap={4}>
					<Typography.Text type={isRead ? 'secondary' : undefined} style={{ fontSize: isSmall ? 14 : 16 }}>
						{isCustom &&
							typeof customNotification === 'object' &&
							'title' in customNotification &&
							customNotification?.title}
						{!isCustom && (notificationTypes?.[type]?.title(brand, offer, source, descriptionExtra) || '')}
					</Typography.Text>

					<Typography.Paragraph
						ellipsis={size === 'sm' || isDashboard ? { rows: 2 } : false}
						style={{ margin: 0, fontSize: isSmall ? 12 : 14, color: isRead ? colorTextQuaternary : colorTextTertiary }}>
						{isCustom && (
							<span
								dangerouslySetInnerHTML={
									typeof customNotification === 'object' && 'body' in customNotification
										? { __html: customNotification.body }
										: undefined
								}></span>
						)}
						{!isCustom && (notificationTypes?.[type]?.text(brand, offer, source, descriptionExtra) || '')}
					</Typography.Paragraph>
				</Flex>

				{!isDashboard && <NotificationDate />}

				{notificationTypes?.[type]?.link && (
					<div>
						{notificationTypes?.[type]?.link(
							brand,
							offer,
							source,
							type === ENotificationTypes.OFFER_INVITE && !!fullOffer && !!fullBrand
								? [
										() => {
											setRejected({ ids: [id], delete_invite: true });
										},
										() => {
											source &&
												setJoinedInviteOffer({ brand: fullBrand, offer: fullOffer, source, notificationId: id });
											setNotificationsModalIsOpened(false);
										},
									]
								: type === ENotificationTypes.BRAND_INVITE && !!fullBrand
									? [
											() => {
												setRejected({ ids: [id], delete_invite: true });
											},
											() => {
												source && setJoinedInviteBrand({ brand: fullBrand, source, notificationId: id });
												setNotificationsModalIsOpened(false);
											},
										]
									: type === ENotificationTypes.NEW_OFFER && !!fullOffer && !!fullBrand
										? [
												() => {
													setOpenedOffer({ brand: fullBrand, offer: fullOffer });
													setNotificationsModalIsOpened(false);
												},
												() => {
													setJoinedOffer({ brand: fullBrand, offer: fullOffer });
													setNotificationsModalIsOpened(false);
												},
											]
										: undefined,
							rejectedIsloading,
						)}
					</div>
				)}
			</Flex>
		</Flex>
	);
};
